/**
 * SeedDrive V2 Navigation Configuration
 *
 * Single source of truth for:
 * - Route paths (ROUTES)
 * - Sidebar navigation structure (NAV_SECTIONS, BOTTOM_SECTIONS)
 * - Pinned quick-access shortcuts (PINNED_SHORTCUTS)
 * - Context-level sub-tab groups (SUB_TAB_GROUPS) used by GlobalHeader
 *
 * Rules:
 * - All route paths live here. Pages and components import from ROUTES — no hardcoded strings.
 * - Adding a new section = add to NAV_SECTIONS only. Sidebar, GlobalHeader, and Routes all derive from this.
 * - Contextual pages (drilldowns, wizards) are in ROUTES but have no nav entry.
 * - Permission arrays use role keys from config/roleConfig.js ROLES constants.
 *   Internal MVP roles: investment_team | ic_member | asset_mgmt_team | finance_team | legal_comp_team | platform_admin
 *   External MVP roles: external_investor | deal_originator | co_investor
 */

import { ROLES } from 'config/roleConfig';

// Roles that can access the Marketplace / Opportunities module
const MARKETPLACE_ACCESS = [
  ROLES.INVESTMENT_TEAM,
  ROLES.EXTERNAL_INVESTOR,
  ROLES.CO_INVESTOR,
  ROLES.DEAL_ORIGINATOR,
  ROLES.PLATFORM_ADMIN,
];

// ─── Route Map ────────────────────────────────────────────────────────────────

export const ROUTES = {
  // Auth / Public
  home:               '/',
  login:              '/login',
  platformOverview:   '/platform-overview',
  howItWorks:         '/how-it-works',
  securityCompliance: '/security-compliance',
  about:              '/about',
  contact:            '/contact',

  // Dashboard
  dashboard:          '/dashboard',

  // Funds
  funds:              '/funds',
  fundDetail:         '/funds/:id',

  // Marketplace
  opportunities:      '/marketplace/opportunities',
  opportunityDetail:  '/marketplace/opportunities/:id',
  dealSubmission:     '/marketplace/submit-deal',    // NEW — Deal Originator submission form

  // Deals (internal)
  pipeline:           '/deals/pipeline',
  dealRecord:         '/deals/:id',              // NEW — Individual deal record
  structuringHub:     '/deals/structuring',
  icSubmission:       '/deals/ic-submission',    // NEW — IC workflow module
  spvDeals:           '/deals/spv-deals',
  spvDashboard:       '/deals/spv',
  spvCreation:        '/deals/spv/new',
  dealWorkspace:      '/deals/workspace',
  investorOnboarding: '/deals/onboarding',

  // Deal Room (external — scoped access for external roles)
  dealRoom:           '/deal-room',              // NEW — External Deal Room

  // Portfolio
  portfolioAssets:    '/portfolio/assets',
  assetDetail:        '/portfolio/assets/:id',
  assetLifecycle:     '/portfolio/lifecycle',
  valuationView:      '/portfolio/valuation',
  portfolioHeatmap:   '/portfolio/heatmap',
  vintageAnalysis:    '/portfolio/vintage',

  // Asset Management
  operationsConsole:  '/asset-management/console',
  oversightHub:       '/asset-management/oversight',
  reviewPacks:        '/asset-management/review-packs',
  riskMonitor:        '/asset-management/risk',
  valueCreation:      '/asset-management/value-creation',
  watchlist:          '/asset-management/watchlist',
  reportBuilder:      '/asset-management/report-builder',

  // Activity
  activityFeed:       '/activity',               // NEW — Activity Feed

  // Operations
  dataRoom:           '/operations/data-room',
  esgReporting:       '/operations/esg',
  governanceRecords:  '/operations/governance',
  counterparties:     '/operations/counterparties',
  auditTrail:         '/operations/audit',

  // Transactions
  tokenisation:       '/transactions/tokenisation',
  tokenHoldings:      '/transactions/holdings',
  tokenTransfer:      '/transactions/transfer',
  tokenAdmin:         '/transactions/admin',
  tokenGovernance:    '/transactions/governance',
  // Token Governance sub-routes — retained for direct linking, removed from sub-nav group
  govProposals:       '/transactions/governance/proposals',
  govAnalytics:       '/transactions/governance/analytics',
  voteDelegation:     '/transactions/governance/delegation',
  govCompliance:      '/transactions/governance/compliance',

  // Communications
  messages:           '/comms/messages',
  notifications:      '/comms/notifications',

  // Administration
  teamManagement:     '/admin/team',
  settings:           '/admin/settings',
  permissions:        '/admin/permissions',
};

// ─── All Internal Roles (convenience array) ───────────────────────────────────
const INTERNAL_ALL = [
  ROLES.INVESTMENT_TEAM,
  ROLES.IC_MEMBER,
  ROLES.ASSET_MGMT_TEAM,
  ROLES.FINANCE_TEAM,
  ROLES.LEGAL_COMP_TEAM,
  ROLES.PLATFORM_ADMIN,
];

const ALL_ROLES = [
  ...INTERNAL_ALL,
  ROLES.EXTERNAL_INVESTOR,
  ROLES.DEAL_ORIGINATOR,
  ROLES.CO_INVESTOR,
];

// ─── Primary Navigation Sections ─────────────────────────────────────────────

export const NAV_SECTIONS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: ROUTES.dashboard,
    icon: 'LayoutDashboard',
    permission: INTERNAL_ALL,
  },
  {
    id: 'funds',
    label: 'Funds',
    path: ROUTES.funds,
    icon: 'Landmark',
    permission: INTERNAL_ALL,
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'Search',
    permission: MARKETPLACE_ACCESS,
    children: [
      {
        id: 'opportunities',
        label: 'Opportunities',
        path: ROUTES.opportunities,
        icon: 'TrendingUp',
        permission: MARKETPLACE_ACCESS,
      },
      {
        id: 'dealSubmission',
        label: 'Submit a Deal',
        path: ROUTES.dealSubmission,
        icon: 'Send',
        permission: [ROLES.DEAL_ORIGINATOR],
      },
    ],
  },
  {
    id: 'deals',
    label: 'Deals',
    icon: 'Briefcase',
    permission: [
      ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
      ROLES.CO_INVESTOR,
    ],
    children: [
      {
        id: 'pipeline',
        label: 'Pipeline',
        path: ROUTES.pipeline,
        icon: 'GitBranch',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'structuringHub',
        label: 'Structuring Hub',
        path: ROUTES.structuringHub,
        icon: 'Scale',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'icSubmission',
        label: 'IC Submissions',
        path: ROUTES.icSubmission,
        icon: 'ClipboardCheck',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'spvDashboard',
        label: 'SPV & Deals',
        path: ROUTES.spvDeals,
        icon: 'Layers',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
          ROLES.CO_INVESTOR,
        ],
      },
      {
        id: 'investorOnboarding',
        label: 'Investor Onboarding',
        path: ROUTES.investorOnboarding,
        icon: 'UserPlus',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN],
      },
    ],
  },
  {
    id: 'portfolio',
    label: 'Portfolio',
    icon: 'PieChart',
    permission: [
      ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM,
      ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
    ],
    children: [
      {
        id: 'portfolioAssets',
        label: 'Portfolio Assets',
        path: ROUTES.portfolioAssets,
        icon: 'Layers',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM,
          ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'assetLifecycle',
        label: 'Asset Lifecycle',
        path: ROUTES.assetLifecycle,
        icon: 'GitBranch',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'valuationView',
        label: 'Valuation View',
        path: ROUTES.valuationView,
        icon: 'DollarSign',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM,
          ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'portfolioHeatmap',
        label: 'Portfolio Heatmap',
        path: ROUTES.portfolioHeatmap,
        icon: 'LayoutGrid',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'vintageAnalysis',
        label: 'Vintage Analysis',
        path: ROUTES.vintageAnalysis,
        icon: 'BarChart2',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
    ],
  },
  {
    id: 'assetManagement',
    label: 'Asset Management',
    icon: 'Building2',
    permission: [
      ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
    ],
    children: [
      {
        id: 'operationsConsole',
        label: 'Operations Console',
        path: ROUTES.operationsConsole,
        icon: 'Monitor',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'oversightHub',
        label: 'Oversight Hub',
        path: ROUTES.oversightHub,
        icon: 'Eye',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'reviewPacks',
        label: 'Review Packs',
        path: ROUTES.reviewPacks,
        icon: 'FileText',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'riskMonitor',
        label: 'Risk Monitor',
        path: ROUTES.riskMonitor,
        icon: 'ShieldAlert',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'valueCreation',
        label: 'Value Creation',
        path: ROUTES.valueCreation,
        icon: 'TrendingUp',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'watchlist',
        label: 'Watchlist',
        path: ROUTES.watchlist,
        icon: 'AlertCircle',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'reportBuilder',
        label: 'Report Builder',
        path: ROUTES.reportBuilder,
        icon: 'FilePlus',
        permission: [ROLES.ASSET_MGMT_TEAM, ROLES.PLATFORM_ADMIN],
      },
    ],
  },
  {
    id: 'activityFeed',
    label: 'Activity',
    path: ROUTES.activityFeed,
    icon: 'Activity',
    permission: INTERNAL_ALL,
  },
  {
    id: 'operations',
    label: 'Compliance & Operations',
    icon: 'Folder',
    permission: [
      ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM,
      ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
    ],
    children: [
      {
        id: 'dataRoom',
        label: 'Document Library',
        path: ROUTES.dataRoom,
        icon: 'FolderLock',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.IC_MEMBER, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM,
          ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'esgReporting',
        label: 'ESG Reporting',
        path: ROUTES.esgReporting,
        icon: 'BarChart3',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'governanceRecords',
        label: 'Governance Records',
        path: ROUTES.governanceRecords,
        icon: 'BookOpen',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM,
          ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'counterparties',
        label: 'Counterparty Directory',
        path: ROUTES.counterparties,
        icon: 'Users',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
      {
        id: 'auditTrail',
        label: 'Audit Trail',
        path: ROUTES.auditTrail,
        icon: 'ClipboardList',
        permission: [
          ROLES.INVESTMENT_TEAM, ROLES.ASSET_MGMT_TEAM, ROLES.FINANCE_TEAM,
          ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN,
        ],
      },
    ],
  },
  {
    id: 'communications',
    label: 'Communications',
    icon: 'MessageSquare',
    permission: ALL_ROLES,
    children: [
      {
        id: 'messages',
        label: 'Messages',
        path: ROUTES.messages,
        icon: 'MessageSquare',
        permission: ALL_ROLES,
        badge: 3,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        path: ROUTES.notifications,
        icon: 'Bell',
        permission: ALL_ROLES,
        badge: 12,
      },
    ],
  },
  {
    id: 'transactions',
    label: 'Transactions',
    icon: 'Coins',
    tag: 'beta',
    permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
    children: [
      {
        id: 'tokenisation',
        label: 'Tokenisation',
        path: ROUTES.tokenisation,
        icon: 'Coins',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'tokenHoldings',
        label: 'Token Holdings',
        path: ROUTES.tokenHoldings,
        icon: 'Wallet',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'tokenTransfer',
        label: 'Token Transfer',
        path: ROUTES.tokenTransfer,
        icon: 'ArrowLeftRight',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'tokenAdmin',
        label: 'Token Administration',
        path: ROUTES.tokenAdmin,
        icon: 'ShieldCheck',
        permission: [ROLES.PLATFORM_ADMIN],
      },
      {
        id: 'tokenGovernance',
        // Flattened: single link replacing 4-item sub-group. Routes still exist.
        label: 'Token Governance',
        path: ROUTES.tokenGovernance,
        icon: 'Vote',
        permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
      },
    ],
  },
  // External-only: Deal Room nav entry (visible to external roles only)
  {
    id: 'dealRoom',
    label: 'Deal Room',
    path: ROUTES.dealRoom,
    icon: 'FolderOpen',
    permission: [ROLES.EXTERNAL_INVESTOR, ROLES.DEAL_ORIGINATOR, ROLES.CO_INVESTOR],
  },
];

// ─── Bottom Navigation ────────────────────────────────────────────────────────

export const BOTTOM_SECTIONS = [
  {
    id: 'teamManagement',
    label: 'Team',
    path: ROUTES.teamManagement,
    icon: 'Users',
    permission: [ROLES.INVESTMENT_TEAM, ROLES.PLATFORM_ADMIN],
  },
  {
    id: 'settings',
    label: 'Settings',
    path: ROUTES.settings,
    icon: 'Settings',
    permission: ALL_ROLES,
  },
  {
    id: 'permissions',
    label: 'Permissions',
    path: ROUTES.permissions,
    icon: 'ShieldCheck',
    permission: [ROLES.LEGAL_COMP_TEAM, ROLES.PLATFORM_ADMIN],
  },
];

// ─── Pinned Quick-Access Shortcuts ────────────────────────────────────────────

export const PINNED_SHORTCUTS = [
  {
    id: 'activeDeals',
    label: 'Active Deals',
    path: ROUTES.pipeline,
    icon: 'Zap',
  },
  {
    id: 'recentOpportunities',
    label: 'Opportunities',
    path: ROUTES.opportunities,
    icon: 'TrendingUp',
  },
  {
    id: 'portfolioSummary',
    label: 'Portfolio',
    path: ROUTES.portfolioAssets,
    icon: 'PieChart',
  },
];

export const ROLE_PINNED_SHORTCUTS = {
  investment_team: [
    { id: 'funds',         label: 'Funds',          path: ROUTES.funds,             icon: 'Landmark' },
    { id: 'activeDeals',   label: 'Active Deals',   path: ROUTES.pipeline,          icon: 'Zap' },
    { id: 'opportunities', label: 'Opportunities',  path: ROUTES.opportunities,     icon: 'TrendingUp' },
  ],
  ic_member: [
    { id: 'icSubmissions', label: 'IC Submissions', path: ROUTES.icSubmission,      icon: 'ClipboardList' },
    { id: 'portfolio',     label: 'Portfolio',      path: ROUTES.portfolioAssets,   icon: 'PieChart' },
    { id: 'activity',      label: 'Activity',       path: ROUTES.activityFeed,      icon: 'Activity' },
  ],
  asset_mgmt_team: [
    { id: 'oversightHub',  label: 'Oversight Hub',  path: ROUTES.oversightHub,      icon: 'Shield' },
    { id: 'reportBuilder', label: 'Report Builder', path: ROUTES.reportBuilder,     icon: 'FilePlus' },
    { id: 'portfolio',     label: 'Portfolio',      path: ROUTES.portfolioAssets,   icon: 'PieChart' },
  ],
  finance_team: [
    { id: 'portfolio',     label: 'Portfolio',      path: ROUTES.portfolioAssets,   icon: 'PieChart' },
    { id: 'valuation',     label: 'Valuation',      path: ROUTES.valuationView,     icon: 'TrendingUp' },
    { id: 'esg',           label: 'ESG Reporting',  path: ROUTES.esgReporting,      icon: 'Leaf' },
  ],
  legal_comp_team: [
    { id: 'governance',    label: 'Governance',     path: ROUTES.governanceRecords, icon: 'FileText' },
    { id: 'dataRoom',      label: 'Data Room',      path: ROUTES.dataRoom,          icon: 'FolderOpen' },
    { id: 'auditTrail',    label: 'Audit Trail',    path: ROUTES.auditTrail,        icon: 'ClipboardList' },
  ],
  platform_admin: [
    { id: 'funds',         label: 'Funds',          path: ROUTES.funds,             icon: 'Landmark' },
    { id: 'activeDeals',   label: 'Active Deals',   path: ROUTES.pipeline,          icon: 'Zap' },
    { id: 'oversightHub',  label: 'Oversight Hub',  path: ROUTES.oversightHub,      icon: 'Shield' },
  ],
  external_investor: [
    { id: 'opportunities', label: 'Opportunities',  path: ROUTES.opportunities,     icon: 'TrendingUp' },
    { id: 'dealRoom',      label: 'Deal Room',      path: ROUTES.dealRoom,          icon: 'Briefcase' },
    { id: 'portfolio',     label: 'Portfolio',      path: ROUTES.portfolioAssets,   icon: 'PieChart' },
  ],
  deal_originator: [
    { id: 'submitDeal',    label: 'Submit Deal',    path: ROUTES.dealSubmission,    icon: 'Plus' },
    { id: 'opportunities', label: 'Opportunities',  path: ROUTES.opportunities,     icon: 'TrendingUp' },
    { id: 'dealRoom',      label: 'Deal Room',      path: ROUTES.dealRoom,          icon: 'Briefcase' },
  ],
  co_investor: [
    { id: 'spvDashboard',  label: 'SPV & Deals',    path: ROUTES.spvDeals,          icon: 'Layers' },
    { id: 'opportunities', label: 'Opportunities',  path: ROUTES.opportunities,     icon: 'TrendingUp' },
    { id: 'dealRoom',      label: 'Deal Room',      path: ROUTES.dealRoom,          icon: 'Briefcase' },
  ],
};

// ─── Sub-Tab Groups ───────────────────────────────────────────────────────────

export const SUB_TAB_GROUPS = {
  assetManagement: [
    { label: 'Operations Console',   path: ROUTES.operationsConsole },
    { label: 'Oversight Hub',        path: ROUTES.oversightHub },
    { label: 'Review Packs',         path: ROUTES.reviewPacks },
    { label: 'Report Builder',       path: ROUTES.reportBuilder },
    { label: 'Watchlist',            path: ROUTES.watchlist },
    { label: 'Value Creation',       path: ROUTES.valueCreation },
    { label: 'Risk Monitor',         path: ROUTES.riskMonitor },
  ],
  portfolio: [
    { label: 'Portfolio Assets',     path: ROUTES.portfolioAssets },
    { label: 'Asset Lifecycle',      path: ROUTES.assetLifecycle },
    { label: 'Valuation View',       path: ROUTES.valuationView },
    { label: 'Portfolio Heatmap',    path: ROUTES.portfolioHeatmap },
    { label: 'Vintage Analysis',     path: ROUTES.vintageAnalysis },
  ],
  deals: [
    { label: 'Pipeline',             path: ROUTES.pipeline,           step: 1, stepLabel: 'Source' },
    { label: 'Structuring Hub',      path: ROUTES.structuringHub,     step: 2, stepLabel: 'Structure' },
    { label: 'IC Submissions',       path: ROUTES.icSubmission,       step: 3, stepLabel: 'Approve' },
    { label: 'SPV & Deals',          path: ROUTES.spvDeals,           step: 4, stepLabel: 'Close' },
    { label: 'Investor Onboarding',  path: ROUTES.investorOnboarding, step: 5, stepLabel: 'Onboard' },
  ],
  transactions: [
    { label: 'Tokenisation',         path: ROUTES.tokenisation },
    { label: 'Token Holdings',       path: ROUTES.tokenHoldings },
    { label: 'Token Transfer',       path: ROUTES.tokenTransfer },
    { label: 'Token Administration', path: ROUTES.tokenAdmin },
    { label: 'Token Governance',     path: ROUTES.tokenGovernance },
  ],
};

// ─── Global Search Index ──────────────────────────────────────────────────────

function flattenSections(sections, category = 'Pages') {
  return sections.flatMap((section) => {
    if (section.path) {
      return [{ label: section.label, path: section.path, category }];
    }
    if (section.children) {
      return flattenSections(section.children, category);
    }
    return [];
  });
}

export const SEARCH_INDEX = [
  ...flattenSections(NAV_SECTIONS),
  ...flattenSections(BOTTOM_SECTIONS),
];
